import type { MatchGroup, MatchItem } from "@/api/types";
import type { ReactNode } from "react";
import { money, percent, shortDate, statusClass } from "@/components/format";

const signedItemMoney = (value: string | number, currency: string) => {
  const numeric = Number(value);
  const sign = numeric > 0 ? "+" : numeric < 0 ? "−" : "";
  return `${sign}${money(Math.abs(numeric), currency)}`;
};

function MatchRecord({ item }: { item: MatchItem }) {
  return <div className="match-record">
    <strong>{item.label}</strong>
    <div className="match-record__facts">
      <time className="mono" dateTime={item.transactionDate}>{shortDate(item.transactionDate)}</time>
      <b className="mono">{item.signedAmount === undefined ? money(item.amount, item.currency) : signedItemMoney(item.signedAmount, item.currency)}</b>
    </div>
  </div>;
}

function CompactMatchRecord({ item, count, dateLabel }: { item: MatchItem; count: number; dateLabel?: string }) {
  return <div className="match-compact__record">
    <strong title={item.label}>{item.label}</strong>
    <span className="match-compact__facts">
      {dateLabel ? <span className="mono">{dateLabel}</span> : <time className="mono" dateTime={item.transactionDate}>{shortDate(item.transactionDate)}</time>}
      <b className="mono">{money(item.amount, item.currency)}</b>
      {count > 1 && <em>+{count - 1}</em>}
    </span>
  </div>;
}

const evidenceValue = (group: MatchGroup, id: string) => String(group.evidence.find((item) => item.id === id)?.value ?? "").trim();
const signedMoney = (value: string | number, currency: string) => `${Number(value) > 0 ? "+" : ""}${money(value, currency)}`;
const evidenceDisplayValue = (id: string, value: string) => id === "aggregation-mode"
  ? value === "BANK_ONE_TO_LEDGER_MANY"
    ? "Un movimiento del extracto contra varios asientos del Mayor"
    : value === "BANK_MANY_TO_LEDGER_MANY"
      ? "Varios movimientos del extracto contra varios asientos del Mayor"
    : "Varios movimientos del extracto contra un asiento del Mayor"
  : value;
const valueToCents = (value: string | number): bigint => {
  const normalized = String(value).trim().replace(",", ".");
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(normalized);
  if (!match) return 0n;
  const fraction = (match[3] ?? "").padEnd(3, "0");
  let cents = BigInt(match[2]) * 100n + BigInt(fraction.slice(0, 2));
  if (Number(fraction[2]) >= 5) cents += 1n;
  return match[1] === "-" ? -cents : cents;
};
const centsToDecimal = (value: bigint) => `${value / 100n}.${String(value % 100n).padStart(2, "0")}`;
const groupedTotal = (items: MatchItem[]) => {
  const signedTotal = items.reduce((sum, item) => sum + valueToCents(item.signedAmount ?? item.amount), 0n);
  return centsToDecimal(signedTotal < 0n ? -signedTotal : signedTotal);
};
const groupedDateRange = (items: MatchItem[]) => {
  const dates = items.map((item) => item.transactionDate).filter((value): value is string => Boolean(value)).sort();
  if (!dates.length) return undefined;
  const first = dates[0];
  const last = dates.at(-1)!;
  return first === last ? shortDate(first) : `${shortDate(first)} – ${shortDate(last)}`;
};

export function MatchSpine({
  group,
  onApprove,
  approvePending = false,
  approveDisabled = false,
  approvalBlockedReason,
  onUnlink,
  onUndo,
  undoLabel = "Deshacer",
  showDifference = false,
  variant = "full",
  detailAddon,
  hiddenEvidenceNames = [],
  maxApprovableDifference,
}: {
  group: MatchGroup;
  onApprove?: () => void;
  approvePending?: boolean;
  approveDisabled?: boolean;
  approvalBlockedReason?: ReactNode;
  onUnlink?: () => void;
  onUndo?: () => void;
  undoLabel?: string;
  showDifference?: boolean;
  variant?: "full" | "compact";
  detailAddon?: ReactNode;
  hiddenEvidenceNames?: readonly string[];
  maxApprovableDifference?: string;
}) {
  const origins = group.items.filter((item) => item.role === "ORIGIN");
  const counterparts = group.items.filter((item) => item.role === "COUNTERPART");
  const visibleEvidence = group.evidence.flatMap((evidence) => {
    if (hiddenEvidenceNames.includes(evidence.name)) return [];
    if (evidence.id !== "aggregation-rounding-tolerance") return [evidence];
    if (group.status === "AUTO") {
      return [{ ...evidence, name: "Tolerancia automática N↔N" }];
    }
    if (maxApprovableDifference === undefined) return [];
    return [{
      ...evidence,
      name: "Tope vigente para aprobación",
      value: maxApprovableDifference,
      passed: Math.abs(Number(group.amountDifference)) <= Number(maxApprovableDifference),
    }];
  });
  const status = approvalBlockedReason
    ? "Diferencia"
    : group.status === "AUTO"
      ? "Auto"
      : group.status === "ADJUSTED"
        ? "Ajustada"
        : group.status;
  if (variant === "compact") {
    const origin = origins[0];
    const counterpart = counterparts[0];
    const aggregation = group.method === "AGGREGATION";
    const inverseAggregation = aggregation && evidenceValue(group, "aggregation-mode") === "BANK_ONE_TO_LEDGER_MANY";
    const manyToManyAggregation = aggregation && evidenceValue(group, "aggregation-mode") === "BANK_MANY_TO_LEDGER_MANY";
    const manualGrouping = group.method === "MANUAL";
    const groupedMatch = aggregation || origins.length > 1 || counterparts.length > 1;
    const compactGroup = (items: MatchItem[], totalEvidenceId: string, manualLabel: string) => items[0] ? {
      ...items[0],
      label: aggregation
        ? evidenceValue(group, "aggregation-rule") || evidenceValue(group, "aggregation-patterns") || items[0].label
        : manualGrouping
          ? `${items.length} ${manualLabel}`
          : items[0].label,
      amount: aggregation ? evidenceValue(group, totalEvidenceId) || groupedTotal(items) : groupedTotal(items),
    } : undefined;
    const originGrouped = aggregation ? !inverseAggregation : origins.length > 1;
    const counterpartGrouped = aggregation ? inverseAggregation || manyToManyAggregation : counterparts.length > 1;
    const compactOrigin = originGrouped
      ? compactGroup(origins, manyToManyAggregation ? "aggregation-bank-total" : "aggregation-total", "movimientos del extracto")
      : origin;
    const compactCounterpart = counterpartGrouped
      ? compactGroup(counterparts, manyToManyAggregation ? "aggregation-ledger-total" : "aggregation-total", "asientos del Mayor")
      : counterpart;
    const originDateLabel = originGrouped ? groupedDateRange(origins) : undefined;
    const counterpartDateLabel = counterpartGrouped ? groupedDateRange(counterparts) : undefined;
    return <details className={`match-compact match-compact--${statusClass(group.status)}`}>
      <summary aria-label={`Ver detalle de conciliación ${group.method === "MANUAL" ? "manual" : "automática"}: ${compactOrigin?.label ?? "origen"} y ${compactCounterpart?.label ?? "contraparte"}`}>
        <div className="match-compact__side">
          <span>Origen</span>
          {compactOrigin && <CompactMatchRecord item={compactOrigin} count={origins.length} dateLabel={originDateLabel} />}
        </div>
        <div className="match-compact__rail">
          <i aria-hidden="true" />
          <span className={`status status--${statusClass(group.status)}`}>{status}</span>
          <b className="mono" aria-label={`Confianza ${percent(group.confidence)}`}>{percent(group.confidence)}</b>
        </div>
        <div className="match-compact__side match-compact__side--right">
          <span>Contraparte</span>
          {compactCounterpart && <CompactMatchRecord item={compactCounterpart} count={counterparts.length} dateLabel={counterpartDateLabel} />}
        </div>
        <div className="match-compact__toolbar">
          {onUndo && (
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onUndo();
              }}
            >
              {undoLabel}
            </button>
          )}
          <span className="panel-collapse-button match-compact__toggle" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </div>
      </summary>
      <div className="match-compact__detail">
        {groupedMatch && <div className="match-compact__grouped-records">
          <div><span>Movimientos del extracto</span>{origins.map((item) => <MatchRecord key={item.id} item={item} />)}</div>
          <div><span>Asientos del Libro Mayor</span>{counterparts.map((item) => <MatchRecord key={item.id} item={item} />)}</div>
        </div>}
        <span className="match-compact__detail-label">Criterios de coincidencia</span>
        <div className="match-footer">
          <div className="evidence-list">
            {showDifference && Number(group.amountDifference) !== 0 && <span className="failed">Diferencia: {money(group.amountDifference, group.currency)}</span>}
            {visibleEvidence.map((evidence) => <span key={evidence.id} className={evidence.passed ? "" : "failed"}>{evidence.name}: {evidenceDisplayValue(evidence.id, evidence.value)}</span>)}
            {group.adjustments.map((adjustment) => (
              <span key={adjustment.id}>
                Redondeo: {signedMoney(adjustment.amount, adjustment.currency)}
                {adjustment.approvedBy ? ` · ${adjustment.approvedBy}` : ""}
                {adjustment.createdAt ? ` · ${shortDate(adjustment.createdAt)}` : ""}
              </span>
            ))}
          </div>
          <div className="row-actions">
            {group.status === "SUGGESTED" && onApprove && (
              <button className="button button--small" disabled={approveDisabled || approvePending} aria-busy={approvePending} onClick={onApprove}>
                {approvePending ? "Aprobando…" : "Aprobar"}
              </button>
            )}
            {["AUTO", "APPROVED"].includes(group.status) && onUnlink && !(group.status === "AUTO" && onUndo) && <button className="button button--ghost button--small" onClick={onUnlink}>Desvincular</button>}
          </div>
        </div>
        {approvalBlockedReason && (
          <div className="match-approval-blocked" role="status">
            <strong>No se puede aprobar esta agrupación.</strong>
            <span>{approvalBlockedReason}</span>
          </div>
        )}
        {detailAddon && <div className="match-compact__addon">{detailAddon}</div>}
      </div>
    </details>;
  }
  return (
    <article className={`match-spine match-spine--${statusClass(group.status)}`}>
      <div className="match-side">
        <span className="match-side__label">Origen</span>
        {origins.map((item) => (
          <MatchRecord key={item.id} item={item} />
        ))}
      </div>
      <div className="match-axis">
        <span className="confidence" aria-label={`Confianza ${percent(group.confidence)}`}>{percent(group.confidence)}</span>
        <i />
      </div>
      <div className="match-side match-side--right">
        <span className="match-side__label">Contraparte</span>
        {counterparts.map((item) => (
          <MatchRecord key={item.id} item={item} />
        ))}
      </div>
      <div className="match-footer">
        <span className={`status status--${statusClass(group.status)}`}>{status}</span>
        <div className="evidence-list">
          {showDifference && Number(group.amountDifference) !== 0 && (
            <span className="failed">Diferencia: {money(group.amountDifference, group.currency)}</span>
          )}
          {visibleEvidence.map((evidence) => (
            <span key={evidence.id} className={evidence.passed ? "" : "failed"}>
              {evidence.name}: {evidenceDisplayValue(evidence.id, evidence.value)}
            </span>
          ))}
          {group.adjustments.map((adjustment) => (
            <span key={adjustment.id}>
              Redondeo: {signedMoney(adjustment.amount, adjustment.currency)}
              {adjustment.approvedBy ? ` · ${adjustment.approvedBy}` : ""}
              {adjustment.createdAt ? ` · ${shortDate(adjustment.createdAt)}` : ""}
            </span>
          ))}
        </div>
        <div className="row-actions">
          {group.status === "SUGGESTED" && onApprove && (
            <button className="button button--small" disabled={approveDisabled || approvePending} aria-busy={approvePending} onClick={onApprove}>
              {approvePending ? "Aprobando…" : "Aprobar"}
            </button>
          )}
          {onUndo && <button type="button" className="button button--ghost button--small" onClick={onUndo}>{undoLabel}</button>}
          {["AUTO", "APPROVED"].includes(group.status) && onUnlink && !(group.status === "AUTO" && onUndo) && (
            <button className="button button--ghost button--small" onClick={onUnlink}>Desvincular</button>
          )}
        </div>
      </div>
      {approvalBlockedReason && (
        <div className="match-approval-blocked" role="status">
          <strong>No se puede aprobar esta agrupación.</strong>
          <span>{approvalBlockedReason}</span>
        </div>
      )}
    </article>
  );
}
